import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, Scale } from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { ShopifyRunSummary } from '../../../../components/warehouses/ShopifyRunSummary';
import { ShopifyScopesNotice } from '../../../../components/warehouses/ShopifyScopesNotice';
import { useNotice } from '../../../../hooks/useNotice';
import { reauthorizeShopify } from '../../../../features/ecommerces/ecommercesThunks';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import { fetchSlotOptions } from '../../../../features/slots/slotsThunks';
import {
  alignmentRowsMarked,
  alignmentSelectionSet,
  setAlignmentFilters,
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import {
  applyStockAlignment,
  fetchStockPreview,
  fetchStockSync,
  fetchStockSyncChannels,
  startStockPreview,
  updateStockSync,
} from '../../../../features/stockSync/stockSyncThunks';
import {
  selectAlignmentCounts,
  selectAlignmentPageRows,
  selectAlignmentSelection,
  selectCanRemoveStock,
  selectFilteredAlignmentRows,
  selectIsRunActive,
  selectLoadSlotOptions,
  selectPreviewState,
  selectRunOf,
  selectUsableChannels,
} from '../../../../features/stockSync/stockSyncSelectors';
import {
  ALIGNMENT_ACTION,
  ALIGNMENT_FILTER,
  RUN_KIND,
  SHOPIFY_ALIGNMENT_PATH,
  STOCK_SYNC_WATCHER,
} from '../../../../features/stockSync/stockSyncConstants';
import {
  PREVIEW_STATE,
  alignedSyncedText,
  alignmentActionText,
  alignmentFilterOptions,
  alignmentNinjaDetail,
  alignmentPublishText,
  alignmentRowNotes,
  alignmentScopesProblemOf,
  alignmentSelectionHint,
  alignmentSelectionText,
  applyBlocker,
  applyConfirmMessage,
  canSyncStock,
  formatSyncDate,
  formatSyncTime,
  isAlignmentRowSelectable,
  pickDefaultChannelId,
  previewSummaryLines,
  previewTokenOf,
  productCount,
  scopesProblemOf,
  shopDomain,
  stockCompareBlocker,
} from '../../../../features/stockSync/stockSyncUtils';
import { selectCurrentWarehouse } from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';
const FIELD_ERROR = 'text-sm font-semibold text-danger';
const BOX = 'rounded-2xl border p-4 text-sm space-y-3';
const WARNING_BOX = `${BOX} border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300`;
const PLAIN_BOX = `${BOX} border-border bg-white text-ninja-dark dark:bg-[#1a1a1a] dark:text-white`;

const STATE_BADGE = {
  [PREVIEW_STATE.RUNNING]: { tone: 'info', label: 'Comparando' },
  [PREVIEW_STATE.READY]: { tone: 'success', label: 'Lista para aplicar' },
  [PREVIEW_STATE.EXPIRED]: { tone: 'warning', label: 'Vencida' },
  [PREVIEW_STATE.APPLIED]: { tone: 'neutral', label: 'Aplicada' },
  [PREVIEW_STATE.FAILED]: { tone: 'danger', label: 'Falló' },
};

const ACTION_TONE = {
  [ALIGNMENT_ACTION.RECEIVE]: 'success',
  [ALIGNMENT_ACTION.REMOVE]: 'danger',
  [ALIGNMENT_ACTION.NONE]: 'neutral',
  [ALIGNMENT_ACTION.SKIP]: 'warning',
};

/**
 * `/warehouse/inventory/shopify/sync?store=<id>`: the last comparison of a
 * Shopify store's stock with Ninja's, a row per product with something to do
 * or to look at. What is marked is applied once: what Ninja lacks comes in as
 * a receipt, what it has over goes out with an adjustment, only of the free
 * stock. A comparison is good for an hour; a new one voids it. The store comes
 * in the address; without it, the page picks it as the dialogs do. The store
 * is the seller's own: when it lacks the permissions, the seller authorizes
 * Ninja again from here and comes back to this page.
 */
export const InventoryShopifySync = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeParam = Number(searchParams.get('store')) || null;

  const warehouse = useSelector(selectCurrentWarehouse);
  const channels = useSelector(selectUsableChannels);
  const { channelsLoaded, selectedId, detail, detailError, alignment } = useSelector((state) => state.stockSync);
  const { preview, filters } = alignment;
  const previewState = useSelector(selectPreviewState);
  const pageRows = useSelector(selectAlignmentPageRows);
  const filteredRows = useSelector(selectFilteredAlignmentRows);
  const counts = useSelector(selectAlignmentCounts);
  const selection = useSelector(selectAlignmentSelection);
  const canRemove = useSelector(selectCanRemoveStock);
  const slotOptions = useSelector(selectLoadSlotOptions);
  const alignmentReport = useSelector(selectRunOf(RUN_KIND.STOCK_ALIGNMENT));
  const aligning = useSelector(selectIsRunActive(RUN_KIND.STOCK_ALIGNMENT));
  const applying = useSelector(selectIsRunning(applyStockAlignment));
  const savingSlot = useSelector(selectIsRunning(updateStockSync, 'initial_load_slot_id'));
  const reauthorizing = useSelector(selectIsRunning(reauthorizeShopify));

  const [searchInput, setSearchInput] = useState(filters.search);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [slotError, setSlotError] = useState(null);
  const [reauthorizeError, setReauthorizeError] = useState(null);
  const setNotice = useNotice('shopify-alignment-notice');

  const syncs = canSyncStock(detail);
  const warehouseId = detail?.channel.warehouse.id;
  const marked = useMemo(() => new Set(selection.ids), [selection.ids]);

  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.ALIGNMENT));
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.ALIGNMENT));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStockSyncChannels({ warehouseId: warehouse?.id ?? null }));
  }, [dispatch, warehouse?.id]);

  // The store in the address is the one in use; without one, the page picks it.
  useEffect(() => {
    if (storeParam) {
      dispatch(stockSyncChannelSelected(storeParam));
      return;
    }
    if (!channelsLoaded) return;
    const settingId = pickDefaultChannelId(channels, { selectedId });
    if (settingId) setSearchParams({ store: String(settingId) }, { replace: true });
  }, [dispatch, storeParam, channelsLoaded, channels, selectedId, setSearchParams]);

  useEffect(() => {
    if (!selectedId) return;
    dispatch(fetchStockSync({ settingId: selectedId }));
    dispatch(fetchStockPreview({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  useEffect(() => {
    if (warehouseId && syncs) dispatch(fetchSlotOptions(warehouseId));
  }, [dispatch, warehouseId, syncs]);

  // The rows are all here: the search only waits for the typing to stop.
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setAlignmentFilters({ search }));
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  const compare = async () => {
    const action = await dispatch(startStockPreview({ settingId: selectedId }));
    if (startStockPreview.rejected.match(action)) setNotice({ type: 'error', message: action.payload });
  };

  const saveSlot = async (slotId) => {
    setSlotError(null);
    const action = await dispatch(
      updateStockSync({ settingId: selectedId, changes: { initial_load_slot_id: Number(slotId) } })
    );
    if (updateStockSync.rejected.match(action)) {
      setSlotError(action.payload?.fieldErrors?.initial_load_slot_id || action.payload?.message || null);
    }
  };

  const apply = async () => {
    setConfirmError(null);
    const action = await dispatch(
      applyStockAlignment({ settingId: selectedId, token: previewTokenOf(preview), listingIds: selection.ids })
    );
    if (applyStockAlignment.fulfilled.match(action)) setConfirming(false);
    else setConfirmError(action.payload);
  };

  const handleReauthorize = async () => {
    setReauthorizeError(null);
    const action = await dispatch(
      reauthorizeShopify({ integrationId: selectedId, returnTo: `${SHOPIFY_ALIGNMENT_PATH}?store=${selectedId}` })
    );
    if (reauthorizeShopify.rejected.match(action)) setReauthorizeError(action.payload);
  };

  const markFiltered = (isMarked) =>
    dispatch(
      alignmentRowsMarked({
        ids: filteredRows
          .filter((row) => !isMarked || isAlignmentRowSelectable(row, canRemove))
          .map((row) => row.listing_id),
        marked: isMarked,
      })
    );

  const scopesProblem = detail && scopesProblemOf(detail);
  const alignmentScopesProblem = detail && alignmentScopesProblemOf(detail);
  const aligned = preview?.result?.summary?.aligned ?? 0;
  // Marking is for applying: without the permissions it cannot go, and the notice says why.
  const editable = previewState === PREVIEW_STATE.READY && !aligning && !scopesProblem && !alignmentScopesProblem;
  const blocker = detail ? applyBlocker({ detail, previewState, selection, aligned }) : null;
  const compareBlocker = detail ? stockCompareBlocker(detail) : 'Elige la tienda';
  const hasRows = Boolean(preview?.result?.rows);
  const location = preview?.result?.location || detail?.location;
  const badge = STATE_BADGE[previewState];

  const columns = [
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div
          className="flex flex-col max-w-[240px]"
          data-testid="shopify-alignment-row"
          data-listing-id={row.listing_id}
          data-action={row.action}
          data-checked={String(marked.has(row.listing_id))}
        >
          {row.product_id ? (
            <Link
              to={`${INVENTORY_PATH}/${row.product_id}`}
              className="font-bold font-mono text-ninja-dark dark:text-white hover:text-ninja-green"
              title="Ver los movimientos de este producto"
            >
              {row.sku}
            </Link>
          ) : (
            <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku}</span>
          )}
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.title}>
            {row.title}
          </span>
        </div>
      ),
    },
    {
      header: 'TIENDA',
      cell: (row) => <span className="tabular-nums">{row.store === null ? '—' : formatQuantity(row.store)}</span>,
    },
    {
      header: 'NINJA',
      cell: (row) => {
        const behind = alignmentNinjaDetail(row);
        return (
          <span
            className={clsx('tabular-nums', behind && 'underline decoration-dotted underline-offset-4 cursor-help')}
            title={behind || undefined}
          >
            {row.ninja === null ? '—' : formatQuantity(row.ninja)}
          </span>
        );
      },
    },
    {
      header: 'AL APLICAR',
      cell: (row) => (
        <ToneBadge tone={ACTION_TONE[row.action]} testId="shopify-alignment-action">
          {alignmentActionText(row)}
        </ToneBadge>
      ),
    },
    {
      // What Ninja publishes now, and after applying.
      header: 'PUBLICA',
      cell: (row) => (
        <span className="tabular-nums" title={row.state_label}>
          {alignmentPublishText(row)}
        </span>
      ),
    },
    {
      header: 'AVISOS',
      cell: (row) => {
        const notes = alignmentRowNotes(row);
        if (!notes.length) return <span className="text-text-muted">—</span>;
        return (
          // The table's cells do not wrap: these texts have to, or they run under the next columns.
          <ul className="flex flex-col gap-1 max-w-[340px]">
            {notes.map((note) => (
              <li
                key={note.key}
                className={clsx(
                  'text-xs whitespace-normal',
                  note.tone === 'warning' ? 'text-amber-700 dark:text-amber-300' : 'text-text-secondary'
                )}
              >
                {note.text}
              </li>
            ))}
          </ul>
        );
      },
    },
  ];

  const isFiltering = filters.search !== '' || filters.action !== ALIGNMENT_FILTER.ALL;
  const emptyMessage = isFiltering
    ? 'No hay productos que coincidan con estos filtros.'
    : 'El stock de Ninja ya está igual al de la tienda: no hay productos que revisar.';

  const recompareButton = (label) => (
    <PendingButton
      pendingOn={startStockPreview}
      variant="outline"
      icon={Scale}
      loadingLabel="Comparando…"
      disabled={Boolean(compareBlocker)}
      title={compareBlocker || undefined}
      onClick={compare}
      data-testid="shopify-alignment-recompare"
    >
      {label}
    </PendingButton>
  );

  return (
    <div data-testid="shopify-alignment-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Inventario" to={INVENTORY_PATH} current="Sincronizar stock" />

      <DetailHero
        testId="shopify-alignment-hero"
        media={<Scale size={36} strokeWidth={1.5} />}
        title="Sincronizar stock con Shopify"
        badges={
          badge && (
            <ToneBadge tone={badge.tone} testId="shopify-alignment-state">
              {badge.label}
            </ToneBadge>
          )
        }
        meta={
          detail && (
            <>
              <span>{detail.channel.company.name}</span>
              <span>{shopDomain(detail.channel.shop_url)}</span>
              <span>
                {detail.channel.warehouse.name}
                {location ? ` ← «${location.name}»` : ' · sin ubicación en la tienda'}
              </span>
              {preview?.finished_at && previewState !== PREVIEW_STATE.FAILED && (
                <span>
                  Comparado {formatSyncDate(preview.finished_at)}
                  {previewState === PREVIEW_STATE.READY && ` · vence a las ${formatSyncTime(preview.result?.expires_at)}`}
                </span>
              )}
            </>
          )
        }
      />

      {detailError && (
        <ErrorState
          variant="inline"
          title="No se pudo leer la tienda"
          description={detailError}
          onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
        />
      )}

      {!selectedId && channelsLoaded && channels.length === 0 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-alignment-none">
          Ningún cliente con el WMS en esta bodega tiene su tienda Shopify conectada.
        </p>
      )}

      {detail && !syncs && (
        <p className={PLAIN_BOX} data-testid="shopify-alignment-forbidden">
          Sincronizar el stock con la tienda lo hace quien guarda mercadería en esta bodega, con el WMS completo.
        </p>
      )}

      {detail && syncs && (
        <>
          <ShopifyScopesNotice
            problem={scopesProblem}
            companyName={detail.channel.company.name}
            onReauthorize={handleReauthorize}
            reauthorizing={reauthorizing}
            error={reauthorizeError}
          />
          {!scopesProblem && (
            <ShopifyScopesNotice
              problem={alignmentScopesProblem}
              companyName={detail.channel.company.name}
              onReauthorize={handleReauthorize}
              reauthorizing={reauthorizing}
              error={reauthorizeError}
              forComparison
            />
          )}

          {previewState === PREVIEW_STATE.NONE && !alignment.loading && (
            <div className={PLAIN_BOX} data-testid="shopify-alignment-empty">
              <p>
                Todavía no se compara el stock de esta tienda. La comparación solo lee: no cambia nada hasta que
                apliques.
              </p>
              {recompareButton('Comparar con la tienda')}
            </div>
          )}

          {previewState === PREVIEW_STATE.RUNNING && (
            <p className={clsx(PLAIN_BOX, 'flex items-center gap-2')} data-testid="shopify-alignment-running">
              <Loader2 size={18} aria-hidden="true" className="shrink-0 animate-spin text-info" />
              Comparando con la tienda… La tabla aparece sola cuando termine.
            </p>
          )}

          {previewState === PREVIEW_STATE.FAILED && (
            <ErrorState
              variant="inline"
              title="La comparación con la tienda falló"
              description={preview.error}
              onRetry={compare}
            />
          )}

          {previewState === PREVIEW_STATE.EXPIRED && (
            <div className={WARNING_BOX} data-testid="shopify-alignment-expired">
              <p className="flex items-start gap-2 font-semibold">
                <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
                La comparación venció a las {formatSyncTime(preview.result?.expires_at)}. Compara de nuevo para
                aplicar: la tienda pudo cambiar desde entonces.
              </p>
              {recompareButton('Comparar de nuevo')}
            </div>
          )}

          {(previewState === PREVIEW_STATE.APPLIED || aligning) && (
            <div className="space-y-3" data-testid="shopify-alignment-applied">
              {previewState === PREVIEW_STATE.APPLIED && (
                <p className="flex items-center gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
                  <CheckCircle2 size={18} aria-hidden="true" className="shrink-0 text-ninja-green" />
                  Esta comparación ya se aplicó · {formatSyncDate(preview.applied_at)}
                </p>
              )}
              <ShopifyRunSummary kind={RUN_KIND.STOCK_ALIGNMENT} report={alignmentReport} />
              {previewState === PREVIEW_STATE.APPLIED && recompareButton('Comparar de nuevo')}
            </div>
          )}

          {hasRows && (
            <>
              <p className="text-sm text-ninja-dark dark:text-white" data-testid="shopify-alignment-summary">
                {previewSummaryLines(preview.result.summary)
                  .map((line) => line.text)
                  .join(' · ')}
              </p>

              <CountChips
                options={alignmentFilterOptions(counts)}
                value={filters.action}
                onChange={(action) => dispatch(setAlignmentFilters({ action }))}
                allValue={ALIGNMENT_FILTER.ALL}
                allLabel="Todos"
                total={counts.all}
                testId="shopify-alignment-filter"
              />

              <FilterBar variant="bare" testId="shopify-alignment-filters">
                <SearchInput
                  variant="bare"
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder="Buscar por SKU o nombre..."
                  testId="shopify-alignment-search"
                />
                {editable && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => markFiltered(true)}
                      disabled={!filteredRows.length}
                      data-testid="shopify-alignment-check-all"
                    >
                      Marcar todos
                    </Button>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => markFiltered(false)}
                      disabled={!filteredRows.length}
                      data-testid="shopify-alignment-uncheck-all"
                    >
                      Desmarcar todos
                    </Button>
                  </div>
                )}
              </FilterBar>

              <DataTable
                columns={columns}
                data={pageRows}
                totalItems={filteredRows.length}
                currentPage={filters.page}
                itemsPerPage={filters.rowsPerPage}
                isLoading={alignment.loading}
                error={alignment.error}
                onRetry={() => dispatch(fetchStockPreview({ settingId: selectedId }))}
                itemLabel="productos"
                emptyMessage={emptyMessage}
                idAccessor="listing_id"
                onPageChange={(page) => dispatch(setAlignmentFilters({ page }))}
                onRowsPerPageChange={(rowsPerPage) => dispatch(setAlignmentFilters({ rowsPerPage }))}
                {...(editable && {
                  selectedRows: selection.ids,
                  onSelectionChange: (ids) => dispatch(alignmentSelectionSet(ids)),
                  isRowSelectable: (row) => isAlignmentRowSelectable(row, canRemove),
                  selectionHint: (row) => alignmentSelectionHint(row, canRemove),
                })}
              />

              {editable && (
                <div
                  className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-2xl border border-border bg-white p-4 shadow-lg dark:bg-[#1a1a1a] md:flex-row md:items-end md:justify-between"
                  data-testid="shopify-alignment-footer"
                >
                  <div className="w-full space-y-1 md:max-w-xs">
                    <FilterableSelect
                      label="Ubicación de entrada"
                      variant="form"
                      value={detail.initial_load_slot?.id ?? ''}
                      onChange={saveSlot}
                      options={slotOptions}
                      placeholder="Elige dónde entra lo recibido"
                      searchPlaceholder="Buscar ubicación..."
                      disabled={savingSlot}
                      hasError={Boolean(slotError)}
                      testId="shopify-alignment-slot"
                    />
                    {slotError && <p className={FIELD_ERROR}>{slotError}</p>}
                  </div>
                  <div className="flex flex-col items-stretch gap-1 md:items-end">
                    <p className="text-sm font-semibold text-ninja-dark dark:text-white" data-testid="shopify-alignment-selection">
                      {counts.all
                        ? alignmentSelectionText(selection)
                        : `Nada que mover: al aplicar, ${alignedSyncedText(aligned)}.`}
                    </p>
                    <Button
                      variant="primary"
                      icon={Scale}
                      disabled={Boolean(blocker)}
                      title={blocker || undefined}
                      onClick={() => {
                        setConfirmError(null);
                        setConfirming(true);
                      }}
                      data-testid="shopify-alignment-apply"
                    >
                      Aplicar
                    </Button>
                    {blocker && (
                      <p className="text-xs text-text-muted md:text-right" data-testid="shopify-alignment-apply-blocker">
                        {blocker}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={apply}
        title={selection.count ? `¿Sincronizar el stock de ${productCount(selection.count)}?` : '¿Sincronizar el stock?'}
        message={applyConfirmMessage({
          selection,
          slot: detail?.initial_load_slot,
          publishing: Boolean(detail?.enabled),
          aligned,
        })}
        variant="primary"
        confirmLabel="Aplicar"
        loadingLabel="Aplicando..."
        loading={applying}
        error={confirmError}
        testId="shopify-alignment-confirm"
      />
    </div>
  );
};
